/**
 * Executes a list of plugins sequentially for a given lifecycle method.
 *
 * Each plugin is invoked one after another, ensuring that the next plugin
 * does not execute until the previous one has completed (supports async methods).
 *
 * This is useful for maintaining predictable execution order when plugins
 * may depend on side effects or shared context.
 *
 * @param {Array<Object>} list - Array of plugin objects.
 * @param {string} method - The lifecycle method to invoke (e.g. 'init', 'start', 'stop').
 * @param {Object} context - Shared context object passed to each plugin method.
 *
 * @returns {Promise<void>} A promise that resolves once all plugins have been executed.
 *
 * @example
 * await runSequential(plugins, 'init', context);
 *
 * @notes
 * - Plugins that do not implement the specified method are safely skipped.
 * - Execution order is preserved based on the array order.
 * - If any plugin throws or rejects, the chain will stop and propagate the error.
 */
export default function runSequential(list, method, context) {
  return list.reduce(
    (promise, plugin) =>
      promise.then(() => plugin?.[method]?.(context)),
    Promise.resolve()
  );
}